import { Link } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Download, Send } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { formatDateOnly, formatMoney, formatStatus } from '@/lib/utils'
import type { PurchaseOrderDetail } from './types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

export function POSpecCreditCard({
  po,
  getAccessToken,
  canSkipSpecCredit,
  handleSkipSpecCredit,
  skippingSpecCredit,
}: {
  po: PurchaseOrderDetail
  getAccessToken: ReturnType<typeof useAuth>['getAccessToken']
  canSkipSpecCredit: boolean
  handleSkipSpecCredit: () => void
  skippingSpecCredit: boolean
}) {
  return (
    <>
      {/* Spec Credit */}
      {po.spec_credit_request && (
        <Card className="gap-0 py-0">
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Spec Credit
              </h3>
              <Badge
                variant={
                  po.spec_credit_request.status === 'responded' ||
                  po.spec_credit_request.status === 'credit_applied'
                    ? 'default'
                    : po.spec_credit_request.status === 'sent' ||
                        po.spec_credit_request.status === 'pending'
                      ? 'secondary'
                      : 'outline'
                }
                className="text-xs"
              >
                {formatStatus(po.spec_credit_request.status)}
              </Badge>
            </div>
            <div className="text-sm space-y-1.5">
              {/* A rep group's grouped request (USA-1553) names no competitor: the column holds the PO's own brand. */}
              {po.spec_credit_request.competitor_manufacturer &&
                po.spec_credit_request.competitor_manufacturer_id !== po.manufacturer_id && (
                <p>
                  Competitor:{' '}
                  <Link
                    to="/manufacturers/$manuId"
                    params={{ manuId: po.spec_credit_request.competitor_manufacturer.uuid }}
                    className="text-primary font-medium hover:underline"
                  >
                    {po.spec_credit_request.competitor_manufacturer.short_name ||
                      po.spec_credit_request.competitor_manufacturer.name}
                  </Link>
                </p>
              )}
              {po.spec_credit_request.deadline_at && (
                <p>
                  Deadline:{' '}
                  <span className="font-medium">
                    {formatDateOnly(po.spec_credit_request.deadline_at)}
                  </span>
                </p>
              )}
              {po.spec_credit_request.response_category && (
                <p>
                  Response:{' '}
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      po.spec_credit_request.response_category === 'credit_offered'
                        ? 'bg-green-100 text-green-800'
                        : po.spec_credit_request.response_category === 'no_credit'
                          ? 'bg-gray-100 text-gray-800'
                          : po.spec_credit_request.response_category === 'question'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {formatStatus(po.spec_credit_request.response_category)}
                  </span>
                </p>
              )}
              {po.spec_credit_request.credit_discount_chain && (
                <p>
                  Discount:{' '}
                  <span className="font-medium">
                    {po.spec_credit_request.credit_discount_chain}
                  </span>
                </p>
              )}
              {po.spec_credit_request.credit_amount && (
                <p>
                  Amount:{' '}
                  <span className="font-medium">
                    {formatMoney(po.spec_credit_request.credit_amount)}
                  </span>
                </p>
              )}
              {po.spec_credit_request.credit_notes && (
                <p className="text-muted-foreground">{po.spec_credit_request.credit_notes}</p>
              )}
              {po.spec_credit_request.quote_pdf_filename && (
                <div className="pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const token = await getAccessToken()
                      if (!token) return
                      const response = await fetch(
                        `${API_BASE_URL}/dashboard/purchase-orders/${po.uuid}/quote-pdf`,
                        { headers: { Authorization: `Bearer ${token}` } },
                      )
                      if (!response.ok) return
                      const blob = await response.blob()
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = po.spec_credit_request!.quote_pdf_filename!
                      a.click()
                      URL.revokeObjectURL(url)
                    }}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download Quote PDF
                  </Button>
                </div>
              )}
              {canSkipSpecCredit && (
                <div className="pt-1">
                  <LoadingButton
                    variant="outline"
                    size="sm"
                    loading={skippingSpecCredit}
                    loadingText="Queuing PO..."
                    onClick={handleSkipSpecCredit}
                  >
                    <Send className="size-3.5" aria-hidden="true" />
                    Skip spec credit &amp; send PO
                  </LoadingButton>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  )
}
